// src/data/resources/articles/getting-started.ts

export const gettingStartedArticles = [
    {
      id: "first-cleaning",
      categoryId: "getting-started",
      title: "Your First Cleaning with MailMaid",
      content: `
        <h2>Getting Started with MailMaid</h2>
        <p>Welcome to MailMaid! This guide will walk you through your first email cleaning session to help you get the most out of our AI-powered email management tools.</p>
        
        <h3>Step 1: Connect Your Email Account</h3>
        <p>Before you can start cleaning, you'll need to connect your email account:</p>
        <ol>
          <li>Click on the "Connect Email" button in the dashboard</li>
          <li>Select your email provider (Gmail, Outlook, etc.)</li>
          <li>Follow the authentication steps to grant MailMaid secure access</li>
          <li>Wait for the initial sync to complete (this may take a few minutes depending on your inbox size)</li>
        </ol>
        
        <h3>Step 2: Review the Dashboard</h3>
        <p>Once connected, you'll see your dashboard with several key sections:</p>
        <ul>
          <li><strong>Email Categories</strong> - Shows your emails organized by type</li>
          <li><strong>Clean-up Suggestions</strong> - AI recommendations for emails to archive or delete</li>
          <li><strong>Priority Inbox</strong> - Important emails that need your attention</li>
          <li><strong>Unsubscribe Center</strong> - Newsletters and promotional emails you can easily unsubscribe from</li>
        </ul>
        
        <h3>Step 3: Run Your First Smart Clean</h3>
        <p>Let's run your first automated cleaning:</p>
        <ol>
          <li>Click on the "Smart Clean" button in the dashboard</li>
          <li>Review the AI recommendations for emails to archive or delete</li>
          <li>Uncheck any emails you want to keep</li>
          <li>Click "Confirm" to process the selected emails</li>
        </ol>
        
        <h3>Step 4: Organize with Categories</h3>
        <p>MailMaid automatically categorizes your emails, but you can fine-tune this:</p>
        <ol>
          <li>Go to the "Categories" section</li>
          <li>Review how your emails have been organized</li>
          <li>Drag and drop any emails that need recategorization</li>
          <li>Create custom categories for your specific needs</li>
        </ol>
        
        <h3>Step 5: Set Up Automation Rules</h3>
        <p>To keep your inbox clean automatically:</p>
        <ol>
          <li>Go to "Settings" > "Automation Rules"</li>
          <li>Click "Add New Rule"</li>
          <li>Define conditions (sender, subject keywords, etc.)</li>
          <li>Choose actions (archive, delete, categorize)</li>
          <li>Save your rule</li>
        </ol>
        
        <p>Congratulations! You've completed your first email cleaning with MailMaid. Your inbox should already feel more organized, and with regular use, MailMaid will learn your preferences and become even more effective.</p>
        
        <p>For more detailed guidance, check out our other resources or contact our support team if you have any questions.</p>
      `,
      excerpt: "We cover your first steps with MailMaid and guide you through your initial email cleanup.",
      featuredImage: "/api/placeholder/600/400",
      order: 1,
      featured: true
    },
    {
      id: "setting-up-email",
      categoryId: "getting-started",
      title: "Setting Up Your Email Account",
      content: `
        <h2>Setting Up Your Email Account with MailMaid</h2>
        <p>Before you can start using MailMaid to manage your inbox, you'll need to connect your email account. This guide walks you through the process for different email providers.</p>
        
        <h3>Supported Email Providers</h3>
        <p>MailMaid currently supports the following email providers:</p>
        <ul>
          <li>Gmail</li>
          <li>Outlook / Office 365</li>
          <li>Yahoo Mail</li>
          <li>iCloud Mail</li>
          <li>ProtonMail</li>
          <li>Any IMAP-enabled email service</li>
        </ul>
        
        <h3>Connection Process</h3>
        <p>Here's how to connect your email account:</p>
        <ol>
          <li>From the MailMaid dashboard, click "Add Email Account"</li>
          <li>Select your email provider from the list</li>
          <li>Follow the authentication flow for your provider</li>
          <li>Grant the required permissions when prompted</li>
          <li>Wait for the initial synchronization to complete</li>
        </ol>
        
        <h3>Security & Permissions</h3>
        <p>When connecting your email account, MailMaid will request certain permissions to function properly:</p>
        <ul>
          <li><strong>Read access</strong> to analyze your emails</li>
          <li><strong>Modify access</strong> to organize, categorize, and clean your inbox</li>
          <li><strong>Send access</strong> (optional) for email response suggestions</li>
        </ul>
        <p>Rest assured that MailMaid uses secure OAuth connections and never stores your email password. All data is encrypted in transit and at rest.</p>
        
        <h3>Setting Up Multiple Accounts</h3>
        <p>If you want to manage multiple email accounts with MailMaid:</p>
        <ol>
          <li>Complete the setup process for your first account</li>
          <li>Go to "Settings" > "Email Accounts"</li>
          <li>Click "Add Another Account"</li>
          <li>Follow the same connection process for each additional account</li>
        </ol>
        <p>You'll be able to switch between accounts easily from the dashboard.</p>
        
        <h3>Troubleshooting Connection Issues</h3>
        <p>If you encounter problems connecting your email account:</p>
        <ul>
          <li>Ensure you're using the correct email address and password</li>
          <li>Check if your email provider has additional security settings (like 2FA or app passwords)</li>
          <li>Temporarily disable any security features that might block the connection</li>
          <li>If using a work email, check with your IT department about OAuth permissions</li>
        </ul>
        
        <p>By following these steps, you'll be ready to start using MailMaid to transform your inbox experience. If you need additional help, please contact our support team.</p>
      `,
      excerpt: "Learn how to connect your email accounts to MailMaid securely and start managing your inbox.",
      order: 2
    },
    {
      id: "dashboard",
      categoryId: "getting-started",
      title: "Understanding the Dashboard",
      content: `
        <h2>Understanding the MailMaid Dashboard</h2>
        <p>The MailMaid dashboard is your command center for email management. This guide helps you understand each section and how to navigate efficiently.</p>
        
        <h3>Dashboard Overview</h3>
        <p>When you first log in to MailMaid, you'll see a dashboard with several key areas:</p>
        
        <h4>1. Email Stats</h4>
        <p>At the top of your dashboard, you'll find important metrics:</p>
        <ul>
          <li><strong>Inbox Count</strong> - Total emails in your inbox</li>
          <li><strong>Priority Emails</strong> - Messages needing immediate attention</li>
          <li><strong>Cleanup Potential</strong> - Number of emails that can be archived or deleted</li>
          <li><strong>Time Saved</strong> - An estimate of time saved through MailMaid's automation</li>
        </ul>
        
        <h4>2. Smart Categories</h4>
        <p>The main section displays your emails organized into AI-powered categories:</p>
        <ul>
          <li><strong>Important</strong> - Critical emails requiring action</li>
          <li><strong>Personal</strong> - Emails from contacts in your address book</li>
          <li><strong>Work</strong> - Business-related communications</li>
          <li><strong>Newsletters</strong> - Subscription emails and updates</li>
          <li><strong>Promotions</strong> - Marketing emails and offers</li>
          <li><strong>Social</strong> - Updates from social networks</li>
          <li><strong>Custom Categories</strong> - Any categories you've created</li>
        </ul>
        
        <h4>3. Quick Actions</h4>
        <p>The sidebar contains buttons for common tasks:</p>
        <ul>
          <li><strong>Smart Clean</strong> - Run an AI-powered inbox cleanup</li>
          <li><strong>Bulk Unsubscribe</strong> - Manage newsletter subscriptions</li>
          <li><strong>Create Rule</strong> - Set up automation for specific emails</li>
          <li><strong>Search</strong> - Find emails with advanced filters</li>
        </ul>
        
        <h4>4. Recent Activity</h4>
        <p>At the bottom, you'll see a log of recent actions:</p>
        <ul>
          <li>Emails archived or deleted</li>
          <li>New rules created</li>
          <li>Categories modified</li>
          <li>Email accounts connected</li>
        </ul>
        
        <h3>Customizing Your Dashboard</h3>
        <p>You can personalize your dashboard to match your workflow:</p>
        <ol>
          <li>Click the "Customize" button in the top-right corner</li>
          <li>Drag and drop widgets to rearrange them</li>
          <li>Toggle visibility for different sections</li>
          <li>Adjust display preferences</li>
          <li>Save your custom layout</li>
        </ol>
        
        <h3>Keyboard Shortcuts</h3>
        <p>Power users can navigate quickly with these shortcuts:</p>
        <ul>
          <li><code>C</code> - Compose new email</li>
          <li><code>A</code> - Archive selected email</li>
          <li><code>D</code> - Delete selected email</li>
          <li><code>/</code> - Focus on search</li>
          <li><code>S</code> - Star/flag email</li>
          <li><code>R</code> - Refresh inbox</li>
          <li><code>?</code> - Show all shortcuts</li>
        </ul>
        
        <p>Take some time to explore the dashboard and familiarize yourself with its features. The more you use MailMaid, the more it learns your preferences and becomes personalized to your email management style.</p>
      `,
      excerpt: "Get familiar with the MailMaid dashboard and learn how to navigate all its features efficiently.",
      order: 3
    },
    {
      id: "quick-start",
      categoryId: "getting-started",
      title: "Quick Start Guide",
      content: `
        <h2>MailMaid Quick Start Guide</h2>
        <p>Need to clean up your inbox quickly? This condensed guide will get you up and running with MailMaid in just a few minutes.</p>
        
        <h3>1. Sign Up & Connect</h3>
        <ul>
          <li>Create your MailMaid account at app.mailmaid.com</li>
          <li>Click "Connect Email" and select your email provider</li>
          <li>Authenticate and grant the necessary permissions</li>
        </ul>
        
        <h3>2. Initial Cleanup</h3>
        <ul>
          <li>Once connected, click "Smart Clean" on the dashboard</li>
          <li>Review the suggestions for emails to archive or delete</li>
          <li>Click "Confirm" to process the selected emails</li>
        </ul>
        
        <h3>3. Manage Subscriptions</h3>
        <ul>
          <li>Go to the "Newsletters" section</li>
          <li>Review your subscription emails</li>
          <li>Use the one-click unsubscribe feature for unwanted newsletters</li>
        </ul>
        
        <h3>4. Set Up Basic Rules</h3>
        <ul>
          <li>Visit "Settings" > "Rules"</li>
          <li>Create a simple rule to auto-archive certain emails</li>
          <li>Example: Archive all receipts to a "Receipts" folder</li>
        </ul>
        
        <h3>5. Daily Use</h3>
        <ul>
          <li>Check your "Priority" folder for important emails</li>
          <li>Use AI response suggestions to reply quickly</li>
          <li>Run "Smart Clean" weekly to maintain inbox zero</li>
        </ul>
        
        <h3>Next Steps</h3>
        <p>Once you're comfortable with the basics, explore these additional features:</p>
        <ul>
          <li>Custom categories for specialized organization</li>
          <li>Email templates for common responses</li>
          <li>Focus mode for distraction-free email management</li>
          <li>Analytics to track your email habits</li>
        </ul>
        
        <p>That's it! You're now ready to start using MailMaid effectively. For more detailed guides, check out our other resources.</p>
      `,
      excerpt: "Get started with MailMaid in just five minutes with this condensed quick start guide.",
      order: 4,
      featured: true
    }
  ];